import type { ReactNode } from 'react';
import {
  Box,
  Circle,
  Columns3,
  Copy,
  Cpu,
  DoorOpen,
  LayoutGrid,
  Minus,
  RotateCcw,
  RotateCw,
  Square,
  Table2,
  Trash2,
  Armchair,
  Coffee,
  Flower2,
  Sofa,
  Gamepad2,
  Presentation,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Separator } from '@/components/ui/Separator';
import { Switch } from '@/components/ui/Switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { cn } from '@/lib/utils';
import {
  applyRect,
  applyRotation,
  canRotate,
  duplicateSelected,
  getSelectedRotation,
  placeLabel,
  patchFurniture,
  patchZone,
  removeSelected,
  type LayoutSelection,
  type PlaceKind,
} from '@/features/digitalOffice/layout';
import {
  FURNITURE_KINDS,
  type FurnitureKind,
  type OfficeLayout,
} from '@/features/digitalOffice/map';
import type { FloorStation } from '@/features/digitalOffice/floor';

const ADD_TOOLS: Array<{ kind: PlaceKind; label: string; icon: typeof Square }> = [
  { kind: 'wall', label: 'Wall', icon: Minus },
  { kind: 'door', label: 'Door', icon: DoorOpen },
  { kind: 'zone', label: 'Room', icon: LayoutGrid },
  { kind: 'rack', label: 'Pallet', icon: Columns3 },
  { kind: 'machine', label: 'Machine', icon: Cpu },
  { kind: 'square', label: 'Square', icon: Square },
  { kind: 'circle', label: 'Circle', icon: Circle },
  { kind: 'station', label: 'Workstation', icon: Box },
  { kind: 'desk', label: 'Desk', icon: Table2 },
  { kind: 'bench', label: 'Bench', icon: Armchair },
  { kind: 'table', label: 'Table', icon: Table2 },
  { kind: 'pingpong', label: 'Ping pong', icon: Gamepad2 },
  { kind: 'counter', label: 'Counter', icon: Square },
  { kind: 'chair', label: 'Chair', icon: Armchair },
  { kind: 'couch', label: 'Couch', icon: Sofa },
  { kind: 'plant', label: 'Plant', icon: Flower2 },
  { kind: 'coffee', label: 'Coffee', icon: Coffee },
  { kind: 'whiteboard', label: 'Board', icon: Presentation },
];

const KIND_LABEL: Record<FurnitureKind, string> = {
  rack: 'Pallet rack',
  desk: 'Desk',
  table: 'Table',
  counter: 'Counter',
  bench: 'Bench',
  plant: 'Plant',
  couch: 'Couch',
  machine: 'Machine',
  chair: 'Chair',
  coffee: 'Coffee bar',
  whiteboard: 'Whiteboard',
  dock: 'Dock',
  square: 'Square',
  circle: 'Circle',
  pingpong: 'Ping pong table',
};

interface LayoutEditorPanelProps {
  layout: OfficeLayout;
  selection: LayoutSelection | null;
  placing: PlaceKind | null;
  stations: FloorStation[];
  saving: boolean;
  dirty: boolean;
  onPlacingChange: (kind: PlaceKind | null) => void;
  onLayoutChange: (layout: OfficeLayout) => void;
  onSelect: (sel: LayoutSelection | null) => void;
  onStationRename?: (id: string, name: string) => void;
  onStationResize?: (id: string, w: number, h: number, rotation?: number) => void;
  onStationRemove?: (id: string) => void;
  onResetDefault: () => void;
  className?: string;
}

export function LayoutEditorPanel({
  layout,
  selection,
  placing,
  stations,
  saving,
  dirty,
  onPlacingChange,
  onLayoutChange,
  onSelect,
  onStationRename,
  onStationResize,
  onStationRemove,
  onResetDefault,
  className,
}: LayoutEditorPanelProps) {
  return (
    <div
      className={cn(
        'flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm',
        className,
      )}
    >
      <div className="border-b border-border px-3 py-2.5">
        <p className="text-sm font-medium text-foreground">Edit the building</p>
        {(saving || dirty) && (
          <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
            {saving ? 'Saving…' : 'Unsaved changes'}
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3">
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Add
        </p>
        <div className="grid grid-cols-3 gap-1.5">
          {ADD_TOOLS.map((t) => {
            const Icon = t.icon;
            const on = placing === t.kind;
            return (
              <button
                key={t.kind}
                type="button"
                onClick={() => onPlacingChange(on ? null : t.kind)}
                title={`Place a ${t.label.toLowerCase()}`}
                className={cn(
                  'flex flex-col items-center gap-1 rounded-lg border px-1 py-2 text-[10px] font-medium transition-colors',
                  on
                    ? 'border-humus bg-secondary text-foreground'
                    : 'border-border bg-background text-muted-foreground hover:border-humus/40 hover:text-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={1.7} />
                {t.label}
              </button>
            );
          })}
        </div>
        {placing && (
          <p className="mt-2 text-[11px] text-muted-foreground">
            Placing {placeLabel(placing).toLowerCase()}…
          </p>
        )}

        <Separator className="my-3" />

        {selection ? (
          <SelectedEditor
            layout={layout}
            selection={selection}
            stations={stations}
            onLayoutChange={onLayoutChange}
            onSelect={onSelect}
            onStationRename={onStationRename}
            onStationResize={onStationResize}
            onStationRemove={onStationRemove}
          />
        ) : null}
      </div>

      <div className="border-t border-border p-3">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={onResetDefault}
          title="Restore the original 230 Stanford Parkway plan"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset to original plan
        </Button>
      </div>
    </div>
  );
}

function SelectedEditor({
  layout,
  selection,
  stations,
  onLayoutChange,
  onSelect,
  onStationRename,
  onStationResize,
  onStationRemove,
}: {
  layout: OfficeLayout;
  selection: LayoutSelection;
  stations: FloorStation[];
  onLayoutChange: (layout: OfficeLayout) => void;
  onSelect: (sel: LayoutSelection | null) => void;
  onStationRename?: (id: string, name: string) => void;
  onStationResize?: (id: string, w: number, h: number, rotation?: number) => void;
  onStationRemove?: (id: string) => void;
}) {
  const station =
    selection.layer === 'station' ? stations.find((s) => s.id === selection.id) : null;
  const furniture =
    selection.layer === 'furniture' ? layout.furniture.find((f) => f.id === selection.id) : null;
  const wall = selection.layer === 'wall' ? layout.walls.find((w) => w.id === selection.id) : null;
  const zone = selection.layer === 'zone' ? layout.zones.find((z) => z.id === selection.id) : null;
  const door = selection.layer === 'door' ? layout.doors.find((d) => d.id === selection.id) : null;

  const rect = station
    ? { x: station.x, y: station.y, w: station.w ?? 32, h: station.h ?? 14 }
    : (furniture ?? wall ?? zone ?? door);

  const title =
    selection.layer === 'station'
      ? 'Workstation'
      : selection.layer === 'wall'
        ? 'Wall'
        : selection.layer === 'zone'
          ? 'Room'
          : selection.layer === 'door'
            ? 'Door'
            : furniture
              ? KIND_LABEL[furniture.kind]
              : 'Piece';

  const onNum = (key: 'x' | 'y' | 'w' | 'h', value: string) => {
    const n = Number(value);
    if (!Number.isFinite(n) || !rect) return;
    if (selection.layer === 'station') {
      if ((key === 'w' || key === 'h') && onStationResize && station) {
        onStationResize(station.id, key === 'w' ? n : rect.w, key === 'h' ? n : rect.h);
      }
      return;
    }
    onLayoutChange(applyRect(layout, selection, { ...rect, [key]: n }));
  };

  const rotation = getSelectedRotation(layout, selection, station?.rotation);
  const setRotation = (deg: number) => {
    if (selection.layer === 'station' && station && rect) {
      onStationResize?.(station.id, rect.w, rect.h, deg);
      return;
    }
    onLayoutChange(applyRotation(layout, selection, deg));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-foreground">{title}</p>
        </div>
      </div>

      {selection.layer === 'station' && station && (
        <Field label="Name">
          <Input
            key={station.id}
            defaultValue={station.name}
            onBlur={(e) => {
              const name = e.target.value.trim();
              if (name && name !== station.name) onStationRename?.(station.id, name);
            }}
            className="h-8 text-sm"
          />
        </Field>
      )}

      {zone && (
        <Field label="Room name">
          <Input
            value={zone.label}
            onChange={(e) => onLayoutChange(patchZone(layout, zone.id, { label: e.target.value }))}
            className="h-8 text-sm"
          />
        </Field>
      )}

      {furniture && (
        <>
          <Field label="Type">
            <Select
              value={furniture.kind}
              onValueChange={(v) =>
                onLayoutChange(patchFurniture(layout, furniture.id, { kind: v as FurnitureKind }))
              }
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FURNITURE_KINDS.filter((k) => k !== 'dock').map((k) => (
                  <SelectItem key={k} value={k}>
                    {KIND_LABEL[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Label">
            <Input
              value={furniture.label ?? ''}
              placeholder="Optional"
              onChange={(e) =>
                onLayoutChange(patchFurniture(layout, furniture.id, { label: e.target.value }))
              }
              className="h-8 text-sm"
            />
          </Field>
          <label className="flex items-center justify-between gap-2 text-xs text-foreground">
            Blocks walking
            <Switch
              checked={furniture.solid !== false}
              onCheckedChange={(v) =>
                onLayoutChange(patchFurniture(layout, furniture.id, { solid: v }))
              }
            />
          </label>
        </>
      )}

      {rect && (
        <div className="grid grid-cols-2 gap-2">
          {(['x', 'y', 'w', 'h'] as const).map((key) => (
            <Field key={key} label={key.toUpperCase()}>
              <Input
                type="number"
                step={1}
                value={Math.round(rect[key])}
                onChange={(e) => onNum(key, e.target.value)}
                className="h-8 text-sm"
                disabled={selection.layer === 'station' && (key === 'x' || key === 'y')}
              />
            </Field>
          ))}
        </div>
      )}

      {canRotate(selection.layer) && (
        <div className="space-y-1">
          <Label className="text-[11px] text-muted-foreground">Rotation</Label>
          <div className="flex gap-1.5">
            <Input
              type="number"
              step={1}
              min={0}
              max={359}
              value={Math.round(rotation)}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (!Number.isFinite(n)) return;
                setRotation(n);
              }}
              className="h-8 flex-1 text-sm"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-2"
              title="Rotate 90° counter-clockwise (Shift+R)"
              onClick={() => setRotation(rotation - 90)}
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-2"
              title="Rotate 90° clockwise (R)"
              onClick={() => setRotation(rotation + 90)}
            >
              <RotateCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        {selection.layer !== 'station' && (
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => {
              const dup = duplicateSelected(layout, selection);
              if (!dup) return;
              onLayoutChange(dup.layout);
              onSelect(dup.selection);
            }}
          >
            <Copy className="h-3.5 w-3.5" />
            Duplicate
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          className="flex-1 text-destructive hover:text-destructive"
          onClick={() => {
            if (selection.layer === 'station') {
              onStationRemove?.(selection.id);
            } else {
              onLayoutChange(removeSelected(layout, selection));
            }
            onSelect(null);
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
          Remove
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <Label className="text-[11px] text-muted-foreground">{label}</Label>
      {children}
    </label>
  );
}
